<!-- @format -->
<template>
    <div class="mdf-registered-deals-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>{{ widgetTitle }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="widget-content widget-content-container">
            <div class="form-row">
                <div class="content">
                    <div v-if="isLoading" class="loading-container"><loading-screen-svg /></div>
                    <div v-for="(deal, index) in deals" v-else :key="index">
                        <div class="pack-name">{{ deal.dealNamePack }}</div>
                        <mdf-registered-deal-card
                            v-for="(dealrequest, indexRequest) in deal.dealRequests"
                            :key="indexRequest"
                            :deal="dealrequest"
                        >
                        </mdf-registered-deal-card>
                    </div>
                    <board-no-records-card
                        v-if="showEmptyState"
                        :title="emptyStateTitle"
                        content="Please add a record to populate this widget"
                    >
                    </board-no-records-card>
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <span />
            <div class="action-container">
                <app-button @click="closeWidget"> Back </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const MdfRegisteredDealCard = () =>
    import('@/components/sliding-widgets/mdf/deals/mdf-registered-deal-card.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const BoardNoRecordsCard = () => import('@/components/board/board-no-records-card');

import { MDFClientRequestFormMixin } from '@/mixins/mdf-client-request-form-mixin';
import LoadingBoundaryMixin from '@/mixins/loading-boundary-mixin';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'mdf-registered-deals-widget',

    components: {
        'app-button': AppButton,
        'board-no-records-card': BoardNoRecordsCard,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'mdf-registered-deal-card': MdfRegisteredDealCard,
    },

    mixins: [MDFClientRequestFormMixin, LoadingBoundaryMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            step: null,
            showEmptyState: false,
            deals: [],
        };
    },

    computed: {
        mdfId() {
            return this.widget.mdfId;
        },

        partnerId() {
            return this.widget.partner.clientId;
        },

        isPartner() {
            return this.widget.isPartner;
        },

        showWonDeals() {
            return this.widget.isWonDeals;
        },

        widgetTitle() {
            if (this.widget.isPartner) {
                if (this.widget.isWonDeals) {
                    return `${this.widget.partner.name}'s Won Deals`;
                } else {
                    return `${this.widget.partner.name}'s Registered Deals`;
                }
            } else if (this.widget.isWonDeals) {
                return 'Won Deals';
            } else {
                return 'Registered Deals';
            }
        },

        emptyStateTitle() {
            return this.widget.isWonDeals ? 'No Won Deals Found' : 'No Registered Deals Found';
        },
    },

    created() {
        this.step = 'edit';

        this.initializeWidget();
    },

    methods: {
        async initializeWidget() {
            this.isLoading = true;

            try {
                let dealsPromise;

                if (this.isPartner) {
                    dealsPromise = this.showWonDeals
                        ? this.getWonDealsByPartner(this.partnerId)
                        : this.getRegisteredDealsByPartner(this.partnerId);
                } else {
                    dealsPromise = this.showWonDeals
                        ? this.getWonDealsById(this.mdfId)
                        : this.getRegisteredDealsById(this.mdfId);
                }

                this.deals = await dealsPromise;

                if (
                    this.deals.length === 0 ||
                    (this.deals[0].dealRequests && this.deals[0].dealRequests.length === 0)
                ) {
                    this.showEmptyState = true;
                }
            } catch (error) {
                const errorMessage = this.showWonDeals
                    ? 'An error occurred trying to get the won deals.'
                    : 'An error occurred trying to get the registered deals.';
                this.$helpers.showFailNotification(errorMessage, { error });
                this.closeWidget();
            }

            this.isLoading = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.mdf-registered-deals-widget .form-row {
    margin-bottom: 20px;
}

.mdf-registered-deals-widget .action-container {
    display: flex;
}

.mdf-registered-deals-widget .action-container .btn {
    margin-left: 15px;
}

.mdf-registered-deals-widget .widget-content {
    width: 100%;
    height: calc(100vh - 152px);
    overflow: auto;
}

.mdf-registered-deals-widget .content .loading-container {
    display: flex;
    justify-content: center;
    width: 100%;
}

.mdf-registered-deals-widget .widget-content.widget-content-container::-webkit-scrollbar {
    width: 9px;
    height: 9px;
    box-shadow: none;
}

.mdf-registered-deals-widget .widget-content.widget-content-container::-webkit-scrollbar-track {
    border-radius: 32px;
    box-shadow: none;
}

.mdf-registered-deals-widget .widget-content.widget-content-container::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: #e9e9ef;
    box-shadow: none;
}

.mdf-registered-deals-widget .widget-content-container {
    padding: 20px 0;
}

.mdf-registered-deals-widget .pack-name {
    font: normal normal 500 16px/24px Roboto;
    margin-left: 32px;
}

.mdf-registered-deals-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}
</style>
